/**
 * Motor de expressões PT-BR (Excel-like) para colunas customizadas de Operações.
 *
 * Sintaxe:
 *   - Variáveis: identificadores em CAIXA ALTA (BLING, SALES_7D, BEST_7D, ...).
 *     Resolvidas contra `row` via VARIABLE_GETTERS; ausentes lançam erro.
 *   - Números: 10, 4.5, 0
 *   - Strings: "texto" ou 'texto' (suporte a escape \")
 *   - Operadores: + - * / < > <= >= = <>  (e unário -)
 *   - Funções: SE, OU, E, NAO, ARRED, ABS, MIN, MAX, PISO, TETO, CRESC, SOMA, MEDIA
 *   - Booleanos: VERDADEIRO, FALSO
 *
 * Implementação:
 *   tokenize → parse (AST) → evaluate
 *
 * SEM `new Function` / `eval` — engine é puro JS interpretado. Isso garante:
 *   1) Sandbox real: usuário não consegue chamar window, document, etc.
 *   2) Curto-circuito correto em SE/OU/E (avalia args lazy).
 *   3) Mensagens de erro PT-BR amigáveis (vs SyntaxError críptico do JS).
 *
 * Precedência (do menor pro maior):
 *   1. Comparação:  <  >  <=  >=  =  <>
 *   2. Aditivo:     +  -
 *   3. Multiplicativo:  *  /
 *   4. Unário:      -x
 *   5. Primário:    número, string, var, chamada, parênteses
 *
 * E/OU/NAO são FUNÇÕES (n-árias), não operadores infixos — é como o Excel faz e
 * casa com o pedido do usuário (`E(a, b, c)`, `OU(x, y)`).
 */

// ============================================================================
// Variáveis built-in
// ============================================================================

const VARIABLE_DESCRIPTIONS = {
    BLING: 'Estoque Bling',
    ML_AVAIL: 'ML disponível',
    ML_UNAVAIL: 'ML indisponível',
    ML_TOTAL: 'ML total',
    STOCK_TOTAL: 'Estoque total (Bling + ML)',
    SALES_TODAY: 'Vendas hoje',
    SALES_YDAY: 'Vendas ontem',
    SALES_7D: 'Vendas 7 dias',
    SALES_30D: 'Vendas 30 dias',
    AVG_7D: 'Média diária 7d',
    BEST_7D: 'Melhor dia 7d',
    COVERAGE: 'Cobertura (dias)',
    FULL_AVG_DAY: 'Média diária (janela do Full)',
    FULL_COVERAGE: 'Cobertura Full (dias; 0 sem venda)',
    FULL_NEED: 'Necessidade de envio Full',
    FULL_SUGGEST: 'Sugestão de envio Full',
    COST: 'Custo',
    PRICE: 'Preço',
    MARGIN: 'Margem %',
};

const VARIABLE_GETTERS = {
    BLING: (r) => r.bling || 0,
    ML_AVAIL: (r) => r.mlAvail || 0,
    ML_UNAVAIL: (r) => r.mlUnavail || 0,
    ML_TOTAL: (r) => r.mlTotal || 0,
    STOCK_TOTAL: (r) => (r.bling || 0) + (r.mlTotal || 0),
    SALES_TODAY: (r) => r.sales_today || 0,
    SALES_YDAY: (r) => r.sales_yday || 0,
    SALES_7D: (r) => r.sales7 || 0,
    SALES_30D: (r) => r.sales30 || 0,
    AVG_7D: (r) => r.avg7 || 0,
    BEST_7D: (r) => r.best7 || 0,
    COVERAGE: (r) => r.coverage || 0,
    FULL_AVG_DAY: (r) => r.fullAvgDay || 0,
    // null (sem demanda) vira 0 — mesma convenção de COVERAGE. Use FULL_AVG_DAY > 0 pra distinguir.
    FULL_COVERAGE: (r) => r.fullCoverage || 0,
    FULL_NEED: (r) => r.fullNeed || 0,
    FULL_SUGGEST: (r) => r.fullSuggest || 0,
    COST: (r) => r.cost || 0,
    PRICE: (r) => r.price || 0,
    MARGIN: (r) => {
        const p = r.price || 0;
        return p === 0 ? 0 : ((p - (r.cost || 0)) / p) * 100;
    },
};

// ============================================================================
// Funções eager (avaliam todos os argumentos antes de chamar)
// ============================================================================

const EAGER_FUNCTIONS = {
    ARRED: (v, d = 0) => {
        const factor = 10 ** (Number(d) || 0);
        return Math.round(Number(v) * factor) / factor;
    },
    ABS: (v) => Math.abs(Number(v)),
    MIN: (...vs) => {
        if (vs.length === 0) return 0;
        return Math.min(...vs.map(Number));
    },
    MAX: (...vs) => {
        if (vs.length === 0) return 0;
        return Math.max(...vs.map(Number));
    },
    PISO: (v) => Math.floor(Number(v)),
    TETO: (v) => Math.ceil(Number(v)),
    CRESC: (curr, prev) => {
        const c = Number(curr);
        const p = Number(prev);
        if (p === 0) return c > 0 ? 100 : 0;
        return ((c - p) / p) * 100;
    },
    SOMA: (...vs) => vs.reduce((s, v) => s + Number(v), 0),
    MEDIA: (...vs) => (vs.length === 0 ? 0 : vs.reduce((s, v) => s + Number(v), 0) / vs.length),
};

// ============================================================================
// Tokenizer
// ============================================================================

const SINGLE_CHAR_OPS = new Set(['+', '-', '*', '/', '<', '>', '=']);

function tokenize(src) {
    const tokens = [];
    const s = String(src ?? '');
    let i = 0;
    while (i < s.length) {
        const ch = s[i];
        // Whitespace
        if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
            i++;
            continue;
        }
        // Punctuation
        if (ch === '(' || ch === ')' || ch === ',') {
            tokens.push({ type: 'punct', value: ch });
            i++;
            continue;
        }
        // Numbers (inteiros e decimais; aceita 4.5 e .5)
        if ((ch >= '0' && ch <= '9') || (ch === '.' && s[i + 1] >= '0' && s[i + 1] <= '9')) {
            let j = i;
            let hasDot = ch === '.';
            j++;
            while (j < s.length) {
                const c = s[j];
                if (c >= '0' && c <= '9') {
                    j++;
                } else if (c === '.' && !hasDot) {
                    hasDot = true;
                    j++;
                } else {
                    break;
                }
            }
            const numStr = s.slice(i, j);
            const num = Number(numStr);
            if (!Number.isFinite(num)) {
                throw new Error(`Número inválido: ${numStr}`);
            }
            tokens.push({ type: 'number', value: num });
            i = j;
            continue;
        }
        // Strings ("..." ou '...')
        if (ch === '"' || ch === "'") {
            const quote = ch;
            let j = i + 1;
            let str = '';
            while (j < s.length && s[j] !== quote) {
                if (s[j] === '\\' && j + 1 < s.length) {
                    str += s[j + 1];
                    j += 2;
                } else {
                    str += s[j];
                    j++;
                }
            }
            if (s[j] !== quote) {
                throw new Error(`String não fechada (esperava ${quote})`);
            }
            tokens.push({ type: 'string', value: str });
            i = j + 1;
            continue;
        }
        // Operadores compostos: <=, >=, <>
        if (ch === '<' && s[i + 1] === '=') {
            tokens.push({ type: 'op', value: '<=' });
            i += 2;
            continue;
        }
        if (ch === '>' && s[i + 1] === '=') {
            tokens.push({ type: 'op', value: '>=' });
            i += 2;
            continue;
        }
        if (ch === '<' && s[i + 1] === '>') {
            tokens.push({ type: 'op', value: '<>' });
            i += 2;
            continue;
        }
        // Operadores simples
        if (SINGLE_CHAR_OPS.has(ch)) {
            tokens.push({ type: 'op', value: ch });
            i++;
            continue;
        }
        // Identificadores (variáveis + nomes de função). Aceita underscore, dígitos
        // após a primeira letra. Letras acentuadas NÃO são aceitas — convenção
        // Excel-like usa identificadores ASCII.
        if ((ch >= 'A' && ch <= 'Z') || (ch >= 'a' && ch <= 'z') || ch === '_') {
            let j = i + 1;
            while (j < s.length) {
                const c = s[j];
                if ((c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9') || c === '_') {
                    j++;
                } else {
                    break;
                }
            }
            tokens.push({ type: 'ident', value: s.slice(i, j) });
            i = j;
            continue;
        }
        throw new Error(`Caractere inesperado: '${ch}' (posição ${i})`);
    }
    return tokens;
}

// ============================================================================
// Parser (recursive descent com precedência)
// ============================================================================

function parse(tokens) {
    let pos = 0;
    const peek = () => tokens[pos];
    const eat = () => tokens[pos++];
    const expect = (type, value) => {
        const t = eat();
        if (!t || t.type !== type || (value !== undefined && t.value !== value)) {
            throw new Error(
                `Esperado ${type}${value !== undefined ? ` '${value}'` : ''}, encontrei ${
                    t ? `'${t.value}'` : 'fim da expressão'
                }`,
            );
        }
        return t;
    };

    function parseExpr() {
        return parseCmp();
    }

    function parseCmp() {
        let left = parseAdd();
        const t = peek();
        if (
            t?.type === 'op' &&
            (t.value === '<' ||
                t.value === '>' ||
                t.value === '<=' ||
                t.value === '>=' ||
                t.value === '=' ||
                t.value === '<>')
        ) {
            eat();
            const right = parseAdd();
            left = { type: 'binop', op: t.value, left, right };
        }
        return left;
    }

    function parseAdd() {
        let left = parseMul();
        while (peek()?.type === 'op' && (peek().value === '+' || peek().value === '-')) {
            const op = eat().value;
            const right = parseMul();
            left = { type: 'binop', op, left, right };
        }
        return left;
    }

    function parseMul() {
        let left = parseUnary();
        while (peek()?.type === 'op' && (peek().value === '*' || peek().value === '/')) {
            const op = eat().value;
            const right = parseUnary();
            left = { type: 'binop', op, left, right };
        }
        return left;
    }

    function parseUnary() {
        if (peek()?.type === 'op' && peek().value === '-') {
            eat();
            return { type: 'unary', op: '-', operand: parseUnary() };
        }
        if (peek()?.type === 'op' && peek().value === '+') {
            eat();
            return parseUnary();
        }
        return parsePrimary();
    }

    function parsePrimary() {
        const t = eat();
        if (!t) throw new Error('Expressão incompleta');
        if (t.type === 'number') return { type: 'number', value: t.value };
        if (t.type === 'string') return { type: 'string', value: t.value };
        if (t.type === 'punct' && t.value === '(') {
            const e = parseExpr();
            expect('punct', ')');
            return e;
        }
        if (t.type === 'ident') {
            if (peek()?.type === 'punct' && peek().value === '(') {
                eat();
                const args = [];
                if (!(peek()?.type === 'punct' && peek().value === ')')) {
                    args.push(parseExpr());
                    while (peek()?.type === 'punct' && peek().value === ',') {
                        eat();
                        args.push(parseExpr());
                    }
                }
                expect('punct', ')');
                return { type: 'call', name: t.value, args };
            }
            return { type: 'var', name: t.value };
        }
        throw new Error(`Token inesperado: '${t.value}'`);
    }

    const result = parseExpr();
    if (pos < tokens.length) {
        throw new Error(`Tokens extras após expressão: '${tokens[pos].value}'`);
    }
    return result;
}

// ============================================================================
// Evaluator
// ============================================================================

function toNumber(v) {
    if (typeof v === 'number') return v;
    if (typeof v === 'boolean') return v ? 1 : 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function looseEquals(a, b) {
    // String vs string: comparação direta (case-sensitive, como Excel).
    if (typeof a === 'string' && typeof b === 'string') return a === b;
    // Boolean vs boolean: direto.
    if (typeof a === 'boolean' && typeof b === 'boolean') return a === b;
    // Caso contrário: compara como número (mais útil que ===: 5 = "5" deve ser true).
    return toNumber(a) === toNumber(b);
}

function evaluate(ast, ctx) {
    switch (ast.type) {
        case 'number':
            return ast.value;
        case 'string':
            return ast.value;
        case 'var': {
            const upper = ast.name.toUpperCase();
            if (upper === 'VERDADEIRO' || upper === 'TRUE') return true;
            if (upper === 'FALSO' || upper === 'FALSE') return false;
            if (Object.hasOwn(ctx.vars, upper)) return ctx.vars[upper];
            // Comportamento Excel-like: variável não definida → 0. Cobre dois
            // casos comuns:
            //   1) row real não tem o campo (produto sem cost/price ainda)
            //   2) formula referencia var derivada não fornecida no contexto
            //      (ex: FORECAST quando não há dailyAll pra calcular)
            // Tradeoff: typos viram 0 silencioso — UI tem lista de variáveis
            // disponíveis pra mitigar. validateExpression() reporta evalNote
            // só em erros REAIS (não em var ausente).
            return 0;
        }
        case 'unary': {
            return -toNumber(evaluate(ast.operand, ctx));
        }
        case 'binop': {
            // Aritméticos avaliam ambos os lados sempre (sem curto-circuito).
            // Comparações idem.
            const a = evaluate(ast.left, ctx);
            const b = evaluate(ast.right, ctx);
            switch (ast.op) {
                case '+':
                    return toNumber(a) + toNumber(b);
                case '-':
                    return toNumber(a) - toNumber(b);
                case '*':
                    return toNumber(a) * toNumber(b);
                case '/': {
                    const nb = toNumber(b);
                    return nb === 0 ? 0 : toNumber(a) / nb;
                }
                case '<':
                    return toNumber(a) < toNumber(b);
                case '>':
                    return toNumber(a) > toNumber(b);
                case '<=':
                    return toNumber(a) <= toNumber(b);
                case '>=':
                    return toNumber(a) >= toNumber(b);
                case '=':
                    return looseEquals(a, b);
                case '<>':
                    return !looseEquals(a, b);
                default:
                    throw new Error(`Operador desconhecido: ${ast.op}`);
            }
        }
        case 'call': {
            const name = ast.name.toUpperCase();
            // Funções lazy (curto-circuito)
            if (name === 'SE') {
                if (ast.args.length !== 3) {
                    throw new Error(`SE espera 3 argumentos, recebeu ${ast.args.length}`);
                }
                const cond = evaluate(ast.args[0], ctx);
                return cond ? evaluate(ast.args[1], ctx) : evaluate(ast.args[2], ctx);
            }
            if (name === 'OU') {
                if (ast.args.length === 0) {
                    throw new Error('OU espera ao menos 1 argumento');
                }
                for (const arg of ast.args) {
                    if (evaluate(arg, ctx)) return true;
                }
                return false;
            }
            if (name === 'E') {
                if (ast.args.length === 0) {
                    throw new Error('E espera ao menos 1 argumento');
                }
                for (const arg of ast.args) {
                    if (!evaluate(arg, ctx)) return false;
                }
                return true;
            }
            if (name === 'NAO') {
                if (ast.args.length !== 1) {
                    throw new Error(`NAO espera 1 argumento, recebeu ${ast.args.length}`);
                }
                return !evaluate(ast.args[0], ctx);
            }
            // Funções eager
            const fn = EAGER_FUNCTIONS[name];
            if (!fn) {
                throw new Error(`Função desconhecida: ${ast.name}`);
            }
            const args = ast.args.map((a) => evaluate(a, ctx));
            return fn(...args);
        }
        default:
            throw new Error(`Nó AST desconhecido: ${ast.type}`);
    }
}

// ============================================================================
// API pública
// ============================================================================

function makeContext(row) {
    const vars = {};
    // Pass 1: chaves UPPERCASE direto da row têm prioridade. Útil pra:
    //   - test harness com vars sintéticas: { PRICE: 100, MARGIN: 55 }
    //   - vars derivadas customizadas que a pipeline injete no futuro
    // Filtra pelo formato identificador (evita pegar lixo).
    if (row && typeof row === 'object') {
        for (const [key, value] of Object.entries(row)) {
            if (/^[A-Z_][A-Z0-9_]*$/.test(key)) {
                vars[key] = value;
            }
        }
    }
    // Pass 2: VARIABLE_GETTERS preenchem o que faltou (chaves lowercase do
    // pipeline real — row.bling, row.sales7, etc).
    for (const [name, getter] of Object.entries(VARIABLE_GETTERS)) {
        if (!(name in vars)) {
            vars[name] = getter(row);
        }
    }
    return { vars };
}

export function EvaluateExpression(expression, row) {
    const tokens = tokenize(expression);
    const ast = parse(tokens);
    return evaluate(ast, makeContext(row || {}));
}

export function ValidateExpression(expression) {
    if (!expression || !String(expression).trim()) {
        return { valid: false, error: 'Expressão vazia' };
    }
    try {
        const tokens = tokenize(expression);
        const ast = parse(tokens);
        // Preview com row vazio — built-ins viram 0, identificadores extras
        // (FORECAST sintético etc) lançam erro. Pra preview, capturamos esse
        // erro e devolvemos só "válido (sintaxe ok)" sem preview.
        try {
            const result = evaluate(ast, makeContext({}));
            return { valid: true, preview: result };
        } catch (evalErr) {
            return { valid: true, preview: null, evalNote: evalErr.message };
        }
    } catch (e) {
        return { valid: false, error: e.message };
    }
}

export function FormatExpressionValue(value, format) {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'VERDADEIRO' : 'FALSO';
    if (typeof value === 'string') return value;
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    if (format === 'percentage') return `${n.toFixed(1)}%`;
    if (format === 'currency') return `R$ ${n.toFixed(2).replace('.', ',')}`;
    if (format === 'number') return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(2);
    return String(value);
}

export const EXPRESSION_VARIABLES = Object.entries(VARIABLE_DESCRIPTIONS).map(([name, desc]) => ({
    name,
    desc,
}));

export const EXPRESSION_FUNCTIONS = [
    { name: 'SE(condição, então, senão)', desc: 'Condicional. Ex: SE(BLING < 10, "BAIXO", "OK")' },
    { name: 'E(a, b, ...)', desc: 'Todos verdadeiros (AND, n-ário, curto-circuito)' },
    { name: 'OU(a, b, ...)', desc: 'Pelo menos um verdadeiro (OR, n-ário, curto-circuito)' },
    { name: 'NAO(v)', desc: 'Negação (NOT)' },
    { name: 'ARRED(valor, casas)', desc: 'Arredondar (ex: ARRED(3.567, 2) = 3.57)' },
    { name: 'PISO(valor)', desc: 'Arredondar para baixo (FLOOR)' },
    { name: 'TETO(valor)', desc: 'Arredondar para cima (CEIL)' },
    { name: 'ABS(valor)', desc: 'Valor absoluto' },
    { name: 'MIN(a, b, ...)', desc: 'Menor valor (n-ário)' },
    { name: 'MAX(a, b, ...)', desc: 'Maior valor (n-ário)' },
    { name: 'SOMA(a, b, ...)', desc: 'Soma (n-ária)' },
    { name: 'MEDIA(a, b, ...)', desc: 'Média aritmética (n-ária)' },
    { name: 'CRESC(atual, anterior)', desc: 'Crescimento % entre dois valores' },
];

// Exports pra teste — não fazem parte da API pública. Não importar do app.
export const __internals = { tokenize, parse, evaluate, makeContext };
